import { Guid } from '@approvalmax/types';
import { constants } from 'modules/common';
import { useEffect, useMemo } from 'react';
import { useGetNetSuiteCustomCompanyFieldsRaw, useGetNetSuiteFields } from 'shared/data/webApp/v1';

import { NetSuiteCustomFieldReferenceItem } from '../types';

const { netSuiteConstants } = constants;

const useCustomFieldReference = (companyId?: Guid) => {
    const { data: rawData, isLoading, error } = useGetNetSuiteFields({ query: { companyId } });

    const data = useMemo(
        () =>
            (rawData?.values || []).reduce((acc, value) => {
                const isIgnoredType = value.type && netSuiteConstants.ignoredCustomFields.includes(value.type);

                if (!isIgnoredType) {
                    acc.push({
                        id: value.field.id,
                        fieldName: value.field.name,
                        fieldScriptId: value.field.scriptId,
                        level: value.level,
                        referenceRecordType: value.referenceRecordType
                            ? {
                                  id: value.referenceRecordType.id,
                                  name: value.referenceRecordType.name,
                              }
                            : null,
                        type: value.type,
                    });
                }

                return acc;
            }, [] as NetSuiteCustomFieldReferenceItem[]),
        [rawData]
    );

    return {
        data,
        isLoading,
        error,
    };
};

const useCustomFieldList = (companyId?: Guid) => {
    return useGetNetSuiteCustomCompanyFieldsRaw(
        { query: { companyId } },
        {
            cacheTime: 0,
        }
    );
};

export const usePageData = (companyId?: string) => {
    const {
        data: customFieldReference,
        isLoading: isLoadingCustomFieldReference,
        error: errorCustomFieldReference,
    } = useCustomFieldReference(companyId);

    const {
        data: customFieldList,
        isLoading: isLoadingCustomFieldList,
        error: errorCustomFieldList,
    } = useCustomFieldList(companyId);

    useEffect(() => {
        if (errorCustomFieldReference) {
            throw errorCustomFieldReference;
        }

        if (errorCustomFieldList) {
            throw errorCustomFieldList;
        }
    }, [errorCustomFieldReference, errorCustomFieldList]);

    return {
        customFieldReference,
        customFieldList,
        isLoading: isLoadingCustomFieldReference || isLoadingCustomFieldList,
    };
};
